import type { HqSocket } from "../../hqAnchors";
import { SOCKET_NAMES, SOCKET_RINGS, type SocketName, type SocketRing } from "./types";

/**
 * Socket frames for the kit's attachments. The sockets themselves are measured offline from each tower's geometry
 * (tools/art/skins/sockets.mjs → art/hq/anchors.json `sockets`); `fromBox` stands in for a model that has none yet
 * (a new tower, a rebake before the tool ran), so a kit never fails to mount, it just mounts on the box.
 *
 * A frame is the socket's point plus three axes in model space: `up`, `out` (away from the wall; the piece's +Z),
 * `right` = up × out (the piece's +X). On a wall it is the face's normal; on the apex, the top and the corners `up`
 * is +Y and `out` points away from the tower's axis (the apex faces the game camera's diagonal).
 *
 * Orientation (2026-10-22): index 3 of every ring (`crest3`, `wall3`, `band3`, `corner3`) is the diagonal the default
 * game camera looks at, index 1 the back. The level camera orbits on the world's −x −z side (cameraCore.ts `place`,
 * yaw 45°) and every tower model is turned by HQ_YAW = π (views/common.ts), so it sees the model's +x +z diagonal
 * (az 135° from the glTF front −z): that is where sockets.mjs measures index 3. The capture sheets, thumbnails and
 * turntable look from the same side (skinThumb.ts `GameSide`).
 */
export interface SocketFrame {
  readonly name: SocketName;
  readonly part: string;
  readonly p: readonly [number, number, number];
  readonly up: readonly [number, number, number];
  readonly out: readonly [number, number, number];
  readonly right: readonly [number, number, number];
}

/**
 * The model-space direction toward the default game camera: the model's +x +z diagonal (the camera sits at world −x −z
 * and the model is turned by HQ_YAW = π). Apex, top and round fitted pieces face it; it is the ring sockets' index 3.
 */
export const CAMERA_FACING: readonly [number, number, number] = [Math.SQRT1_2, 0, Math.SQRT1_2];

const norm = (v: readonly number[]): [number, number, number] => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };
const cross = (a: readonly number[], b: readonly number[]): [number, number, number] => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];

/** The frame of one stored socket. */
export function frameOf(name: SocketName, s: HqSocket): SocketFrame {
  const n = norm(s.n);
  const vertical = Math.abs(n[1]) > 0.9;
  let up: [number, number, number], out: [number, number, number];
  if (vertical) {
    up = [0, 1, 0];
    const flat = Math.hypot(s.p[0], s.p[2]);
    out = flat > 0.05 && name !== "apex" && name !== "top" ? norm([s.p[0], 0, s.p[2]]) : [...CAMERA_FACING] as [number, number, number];
  } else {
    up = [0, 1, 0];
    out = norm([n[0], 0, n[2]]);
  }
  return { name, part: s.part, p: [s.p[0], s.p[1], s.p[2]], up, out, right: cross(up, out) };
}

/**
 * Diagonal azimuths of the ring sockets by index, as sockets.mjs measures them (degrees from the glTF front −z toward
 * +x): index 3 (az 135°, model +x +z) faces the game camera, index 1 (az 315°) is the back.
 */
export const RING_AZ: readonly number[] = [225, 315, 45, 135];

/** Box-derived sockets for a model the tool has not measured: the box's top, its sides at the diagonals. */
export function fromBox(box: { min: readonly number[]; max: readonly number[] }): Record<SocketName, HqSocket> {
  const cx = (box.min[0] + box.max[0]) / 2, cz = (box.min[2] + box.max[2]) / 2, h = box.max[1] - box.min[1];
  const hx = (box.max[0] - box.min[0]) / 2, hz = (box.max[2] - box.min[2]) / 2;
  const at = (az: number, k: number, y: number, part: string): HqSocket => {
    const r = (az * Math.PI) / 180, d = [Math.sin(r), 0, -Math.cos(r)];
    return { part, p: [cx + d[0] * hx * k, y, cz + d[2] * hz * k], n: d };
  };
  const out = {} as Record<SocketName, HqSocket>;
  out.apex = { part: "roof", p: [cx, box.max[1], cz], n: [0, 1, 0] };
  out.top = { part: "drum", p: [cx, box.min[1] + h * 0.6, cz], n: [0, 1, 0] };
  RING_AZ.forEach((az, i) => {
    out[`crest${i}` as SocketName] = at(az, 0.55, box.min[1] + h * 0.62, "roof");
    out[`wall${i}` as SocketName] = at(az, 0.62, box.min[1] + h * 0.18, "drum");
    out[`band${i}` as SocketName] = at(az, 0.6, box.min[1] + h * 0.34, "drum");
    const c = at(az, 0.75, box.min[1] + 0.05, "plinth");
    out[`corner${i}` as SocketName] = { ...c, n: [0, 1, 0] };
  });
  return out;
}

/** Expands a use's `at` list (names and rings) into socket names, in order, without repeats. */
export function expandAt(at: readonly (SocketName | SocketRing)[]): SocketName[] {
  const out: SocketName[] = [];
  for (const a of at) {
    const names = (SOCKET_RINGS as readonly string[]).includes(a) ? [0, 1, 2, 3].map((i) => `${a}${i}` as SocketName) : [a as SocketName];
    for (const n of names) if (!out.includes(n)) out.push(n);
  }
  return out;
}

/** Every socket of a model: the measured ones, any missing filled from the box. */
export function socketsFor(measured: Readonly<Record<string, HqSocket>> | undefined, box: { min: readonly number[]; max: readonly number[] }): Record<SocketName, SocketFrame> {
  const fallback = fromBox(box);
  const out = {} as Record<SocketName, SocketFrame>;
  for (const name of SOCKET_NAMES) out[name] = frameOf(name, measured?.[name] ?? fallback[name]);
  return out;
}
